/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import { isNone } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type Goal from '@linc-technologies/linced-common/models/goals/goal';
import type Subcategory from '@linc-technologies/linced-common/models/goals/subcategory';
import type Subject from '@linc-technologies/linced-common/models/goals/subject';
import type School from '@linc-technologies/linced-common/models/school';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

// Proto: goals.GoalCategory.
export default class Category extends Model {
  declare [Type]: 'goals/category';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @belongsTo('goals/subject', { async: true, inverse: 'categories' })
  declare subjectId: AsyncBelongsTo<Subject>;

  @alias('subjectId')
  declare subject: AsyncBelongsTo<Subject>;

  @alias('subject.id')
  declare subject_id: string | null | undefined;

  @attr('boolean', { defaultValue: false })
  declare isPLP: boolean;

  @hasMany('goals/subcategory', { async: true, inverse: 'categoryId' })
  declare subcategories: AsyncHasMany<Subcategory>;

  @computed('subcategories.content.[]')
  get subcategoriesArray() {
    return resolved(this.subcategories);
  }

  subcategorySortOrder = Object.freeze(['sequence', 'label']);

  @sort('subcategoriesArray', 'subcategorySortOrder')
  declare sortedSubcategories: unknown[];

  @computed('sortedSubcategories.[]')
  get sortedBoundSubcategories() {
    // drag-sort reads the owner from the list.
    const subcategories: unknown[] & { __category__?: Category } =
      this.sortedSubcategories.slice();
    subcategories.__category__ = this;
    return subcategories;
  }

  @hasMany('goals/goal', { async: true, inverse: 'categoryId' })
  declare goals: AsyncHasMany<Goal>;

  // Goals directly under the category. A goal with no subcategory reads its
  // subcategory_id as null so match on both null + undefined.
  @computed('goals.content.@each.subcategory_id')
  get filteredGoals() {
    return resolved(this.goals).filter((goal) =>
      isNone(goal.get('subcategory_id')),
    );
  }

  goalSortOrder = Object.freeze(['sequence', 'label']);

  @sort('filteredGoals', 'goalSortOrder')
  declare sortedGoals: unknown[];

  @computed('sortedGoals.[]')
  get sortedBoundGoals() {
    // drag-sort reads the owner from the list.
    const goals: unknown[] & { __category__?: Category } =
      this.sortedGoals.slice();
    goals.__category__ = this;
    return goals;
  }

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('string', { defaultValue: '' })
  declare templateId: string;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('string', { defaultValue: '' })
  declare key: string;

  @attr('string', { defaultValue: '' })
  declare badgeMediaId: string;

  @alias('badgeMediaId')
  declare badgeMedia: string | undefined;

  @attr('string', { defaultValue: '' })
  declare badgeDescription: string;
}
